import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchById, handleError, formatCurrency } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentsIcon from "@mui/icons-material/Payments";
import ViewPage from './ViewPage';
import axios from 'axios';
import { useSnackbar } from "../../providers/SnackBarProvider";
import useNavigationHelperStore from '../../store/navigationHelperStore';
import usePurchaseStore from '../../store/purchaseStore';

function ViewProvider() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavigation } = useNavigationHelperStore();
  const [id, setId] = useState(location.state.id);
  const [provider, setProvider] = useState(null);
  const {updateTitle} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);
  const {config, isLoadingConfig} = useConfig();
  const snackbar = useSnackbar();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });
  const [addPaymentDialog, setAddPaymentDialog] = useState({
    open: false,
    provider: null,
    amount: '',
    paymentType: null,
  });
  const { resetPurchase } = usePurchaseStore();
  const { t } = useTranslation();

  useEffect(() => { 
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const provider = await fetchById(id, 'products_providers');
        setProvider(provider);
        updateTitle(provider.thumb_url || '', provider.name, provider.email);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);
  
  const handleEdit = () => {
    setNavigation('/main/view-provider', { state: { id: provider.id }});
    navigate('/main/edit-provider', { state: { updateProvider: provider } });
  };

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}products_providers?id=${provider.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = () => {
    resetPurchase();
    setNavigation('/main/view-provider', { state: { id: provider.id }});
    navigate('/main/edit-purchase', { state: { provider } });
  };

  const handleViewDebtDetail = (provider) => {
    navigate('/main/view-provider-debts', { 
      state: { 
        id: provider.id,
        name: provider.name,
        thumb_url: provider.thumb_url
      } 
    });
  };

  const handleAddPayment = async (provider) => {
    try {
      let currentPaymentTypes = paymentTypes;
      if (paymentTypes.length === 0) {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}finance?payment_types`,
          { withCredentials: true }
        );
        currentPaymentTypes = response.data.data;
        setPaymentTypes(currentPaymentTypes);
      }

      const cashAliases = [
        t('common.paymentCashSynonymEs'),
        t('common.paymentCashSynonymEn'),
        t('common.paymentCashSynonymPt'),
      ].map((s) => s.toLowerCase().trim());
      const cashPaymentType =
        currentPaymentTypes.find((type) =>
          cashAliases.includes(type.name.toLowerCase().trim())
        ) || null;
      
      setAddPaymentDialog({
        open: true,
        provider: provider,
        amount: provider.balance.toString(),
        paymentType: cashPaymentType
      });
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handleAddPaymentClose = () => {
    setAddPaymentDialog({
      open: false,
      provider: null,
      amount: '',
      paymentType: null
    });
  };

  const handleAmountChange = (event) => {
    setAddPaymentDialog(prev => ({
      ...prev,
      amount: event.target.value
    }));
  };

  const handlePaymentTypeChange = (event, newValue) => {
    setAddPaymentDialog(prev => ({
      ...prev,
      paymentType: newValue
    }));
  };

  const handleAddPaymentConfirm = async () => {
    try {
      if (!addPaymentDialog.paymentType) {
        snackbar(t('reports.pleaseSelectPaymentMethod'));
        return;
      }

      if (!addPaymentDialog.amount || parseFloat(addPaymentDialog.amount) <= 0) {
        snackbar(t('reports.pleaseEnterValidAmount'));
        return;
      }

      setIsLoading(true);
      
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}purchases_payments`,
        {
          provider: {
            id: addPaymentDialog.provider.id 
          },
          finance_payment_method: {
            id: addPaymentDialog.paymentType.id
          },
          amount: addPaymentDialog.amount,
        },
        { withCredentials: true }
      );
      
      if (response.data.status === 'SUCCESS') {
        // After processing, refresh provider data
        const updatedProvider = await fetchById(id, 'products_providers');
        setProvider(updatedProvider);
        handleAddPaymentClose();
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || isLoadingConfig || !provider) {
    return (
      <>
        <CircularProgress
          size={42}
          sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
        />
      </>
    );
  }

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      {/* Provider states Section - Display balance/debt */}
      {provider.balance !== null && provider.balance > 0 && (     
        <Box
          style={{
            marginBottom: '8px',
            marginTop: '16px',
            padding: '8px',
            background: '#fff',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            gap: '8px',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Typography variant="caption">
              {t('view.providerDebtLabel', { amount: formatCurrency(provider.balance) })}
            </Typography>
            <Box>
              <Tooltip title={t('common.viewDetail')}>
                <IconButton
                  size="small"
                  onClick={() => handleViewDebtDetail(provider)}
                  sx={{ color: 'text.secondary', mr: 1 }}
                >
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('reports.payment')}>
                <IconButton
                  size="small"
                  onClick={() => handleAddPayment(provider)}
                  sx={{ color: 'text.secondary' }}
                >
                  <PaymentsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}

      {/* Info Section */}
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.name')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {provider.name}
        </Typography>
      </Box>

      {/* Text fields with optional visibility */}
      {provider.address && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.address')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {provider.address}
          </Typography>
        </Box>
      )}
      
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.region')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {provider.region.friendly_name}
        </Typography>
      </Box>

      {provider.phone && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.phone')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {provider.phone}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.email')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {provider.email}
        </Typography>
      </Box>

      {provider.web_page && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.webPage')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {provider.web_page}
          </Typography>
        </Box>
      )}

      {provider.notes && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.notes')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {provider.notes}
          </Typography>
        </Box>
      )}

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.confirmDeleteProviderTitle', { name: provider?.name })}</DialogTitle>
        <DialogContent>
          {t('view.confirmDeleteProviderMessage', { name: provider?.name })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog
        open={addPaymentDialog.open}
        onClose={handleAddPaymentClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('view.makePayment')}</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="subtitle1">
                {t('view.providerPaymentProvider', { name: addPaymentDialog.provider?.name })}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {addPaymentDialog.provider
                  ? t('view.providerCurrentDebt', { amount: formatCurrency(addPaymentDialog.provider.balance) })
                  : ''}
              </Typography>
              
              <Autocomplete
                fullWidth
                options={paymentTypes}
                getOptionLabel={(option) => option?.name || ""}
                value={addPaymentDialog.paymentType}
                onChange={handlePaymentTypeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('common.paymentMethod')}
                    required
                  />
                )}
              />
              
              <TextField
                label={t('view.paymentAmountToPay')}
                type="number"
                value={addPaymentDialog.amount}
                onChange={handleAmountChange}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleAddPaymentClose}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleAddPaymentConfirm} 
            variant="contained" 
            color="primary"
            disabled={isLoading || !addPaymentDialog.paymentType || !addPaymentDialog.amount}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'), 
            buttons: [
              ...(config.permissions.actions_permissions.purchases.create ? [{
                label: t('view.purchaseAction'),
                action: handlePurchase,
                icon: <ShoppingCartOutlined />
              }] : []),
            ].filter(Boolean)
          },
          {
            label: t('view.supplierSection'),
            buttons: [
              ...(config.permissions.actions_permissions.purchases.update ? [{
                label: t('view.vetActionEdit'),
                action: handleEdit,
                icon: <EditIcon />
              }] : []),
              ...(config.permissions.actions_permissions.purchases.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          },
        ]}
      />

    </ViewPage>
  );
}

export default ViewProvider;